import {
  Box,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { formatCitysprintQuoteTimeLabel } from '../../../helpers/citysprintQuoteBuckets';
import {
  getVehicleCardOrientation,
  getVehicleGridTemplateColumns,
} from '../helpers/vehicleTypeCards';
import { CollectionVehicleCard } from './CitysprintVehicleTypeCards';
import { CitysprintExtendedCoverSection } from './CitysprintExtendedCoverSection';

function CitysprintVehicleTypePanel({
  vehicleCards,
  vehicleCount,
  vehicleOrientation,
  selectedVehicleIndex,
  onSelectVehicle,
}) {
  return (
    <Paper
      component='section'
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant='h5'
        component='h2'
        sx={{
          mb: 2,
          color: 'text.primary',
          fontSize: { xs: '1.125rem', sm: '1.25rem' },
          letterSpacing: 0,
        }}
      >
        Choose your vehicle type
      </Typography>
      <FormControl component='fieldset' variant='standard' fullWidth>
        <RadioGroup
          aria-label='CitySprint vehicle type'
          name='citysprint-parcel-vehicle'
          value={
            selectedVehicleIndex != null ? String(selectedVehicleIndex) : ''
          }
          sx={{
            display: 'grid',
            width: '100%',
            gridTemplateColumns: getVehicleGridTemplateColumns(vehicleCount),
            gap: { xs: 1.25, sm: 1.5 },
            alignItems: 'stretch',
            '& .MuiFormControlLabel-root': {
              height: '100%',
            },
          }}
          onChange={e => {
            const index = Number(e.target.value);
            if (Number.isFinite(index)) {
              onSelectVehicle?.(index);
            }
          }}
        >
          {vehicleCards.map(vc => (
            <CollectionVehicleCard
              key={vc.selectionKey}
              vehicle={vc.vehicle}
              vehicleIndex={vc.vehicleIndex}
              orientation={vehicleOrientation}
              selected={selectedVehicleIndex === vc.vehicleIndex}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </Paper>
  );
}

function CitysprintServiceQuotePanel({
  serviceCards,
  selectedOptionKey,
  onSelectOption,
}) {
  return (
    <Paper
      component='section'
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant='h3' component='h2' sx={{ mb: 1.5 }}>
        Select service
      </Typography>
      <FormControl component='fieldset' variant='standard' fullWidth>
        <RadioGroup
          aria-label='CitySprint service'
          name='citysprint-parcel-service'
          value={selectedOptionKey || ''}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
          onChange={e => {
            const key = String(e.target.value ?? '').trim();
            const card = serviceCards.find(
              c => String(c.selectionKey ?? '').trim() === key
            );
            if (card) {
              onSelectOption(card);
            }
          }}
        >
          {serviceCards.map(card => {
            const collect = formatCitysprintQuoteTimeLabel(
              card.latestCollectionTime
            );
            const deliver = formatCitysprintQuoteTimeLabel(
              card.latestDeliveryTime
            );
            const selected =
              String(selectedOptionKey ?? '').trim() ===
              String(card.selectionKey ?? '').trim();
            return (
              <FormControlLabel
                key={card.selectionKey}
                value={card.selectionKey}
                sx={{
                  m: 0,
                  alignItems: 'stretch',
                  borderRadius: 1.5,
                  border: '2px solid',
                  borderColor: selected ? 'primary.main' : 'divider',
                  bgcolor: selected ? 'action.hover' : 'background.paper',
                  px: 1.5,
                  py: 1.25,
                  transition: t =>
                    t.transitions.create(['border-color', 'background-color'], {
                      duration: 150,
                    }),
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                  '& .MuiFormControlLabel-label': {
                    flex: 1,
                    width: '100%',
                    minWidth: 0,
                  },
                }}
                control={<Radio sx={{ alignSelf: 'flex-start', mt: 0.25 }} />}
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: { sm: 'space-between' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: { xs: 1, sm: 2 },
                      width: '100%',
                      py: 0.25,
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: { sm: 1 } }}>
                      <Typography
                        variant='subtitle1'
                        component='span'
                        sx={{ fontWeight: 400, display: 'block' }}
                      >
                        {card.serviceTitle || card.section.title}
                      </Typography>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mt: 0.5 }}
                      >
                        Collect by{' '}
                        <Box component='span' sx={{ fontWeight: 500 }}>
                          {collect}
                        </Box>
                        {' · '}
                        Deliver by{' '}
                        <Box component='span' sx={{ fontWeight: 500 }}>
                          {deliver}
                        </Box>
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flexShrink: 0,
                        textAlign: { xs: 'left', sm: 'right' },
                      }}
                    >
                      <Typography
                        component='span'
                        sx={{
                          fontSize: '1.2rem',
                          fontWeight: 400,
                          color: 'primary.main',
                        }}
                      >
                        £{card.prices.incl}
                      </Typography>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        component='div'
                      >
                        incl VAT
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            );
          })}
        </RadioGroup>
      </FormControl>
    </Paper>
  );
}

// eslint-disable-next-line max-lines-per-function
export function CitysprintVehicleQuotePanel({
  quoteLayout,
  selectedVehicleIndex,
  onSelectVehicle,
  hideServiceCards,
  onSelectOption,
  selectedOptionKey,
}) {
  const vehicleCards = quoteLayout?.vehicleCards ?? [];
  const byVi = quoteLayout?.serviceCardsByVehicleIndex ?? [];
  const hasVehicles = vehicleCards.length > 0;
  const showQuoteUi = hasVehicles && !hideServiceCards;
  const serviceCards =
    selectedVehicleIndex != null ? byVi[selectedVehicleIndex] ?? [] : [];
  const vehicleCount = vehicleCards.length;
  const vehicleOrientation = getVehicleCardOrientation(vehicleCount);

  return (
    <Box
      role='region'
      aria-label='CitySprint service and vehicle options'
      sx={{ mt: 2 }}
    >
      {showQuoteUi ? (
        <Stack spacing={3}>
          <CitysprintVehicleTypePanel
            vehicleCards={vehicleCards}
            vehicleCount={vehicleCount}
            vehicleOrientation={vehicleOrientation}
            selectedVehicleIndex={selectedVehicleIndex}
            onSelectVehicle={onSelectVehicle}
          />

          {selectedVehicleIndex != null && serviceCards.length ? (
            <CitysprintServiceQuotePanel
              serviceCards={serviceCards}
              selectedOptionKey={selectedOptionKey}
              onSelectOption={onSelectOption}
            />
          ) : null}

          {showQuoteUi && selectedOptionKey ? (
            <CitysprintExtendedCoverSection />
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
}

export function CitysprintQuoteSummarySection({
  quoteLayout,
  selectedVehicleIndex,
  onSelectVehicle,
  hideServiceCards,
  onSelectOption,
  selectedOptionKey,
}) {
  return (
    <Box id='citysprint-quote-summary' sx={{ scrollMarginTop: 2 }}>
      <CitysprintVehicleQuotePanel
        quoteLayout={quoteLayout}
        selectedVehicleIndex={selectedVehicleIndex}
        onSelectVehicle={onSelectVehicle}
        hideServiceCards={hideServiceCards}
        onSelectOption={onSelectOption}
        selectedOptionKey={selectedOptionKey}
      />
    </Box>
  );
}
